import { DeleteIcon } from "@chakra-ui/icons";
import { FC, useCallback, useState } from "react";

import { RevokeIcon } from "@nens/ranalib";
import { ThreediModel } from "@nens/threedi/openapi-client";

import AlertBox from "../../components/AlertBox";
import ButtonWithConfirmation from "../../components/ButtonWithConfirmation";
import {
  deleteThreediModel,
  recreateThreediModel,
  useModelTasks,
} from "../../queries/threedimodels";

import TasksTable from "./TasksTable";

interface ModelWithoutSuccessProps {
  callbackWhenModelDeleted: () => void;
  model: ThreediModel;
}

// Model with isValid false; show results of tasks.

const ModelWithoutSuccess: FC<ModelWithoutSuccessProps> = ({ callbackWhenModelDeleted, model }) => {
  const [deleteModelErrorMessages, setDeleteModelErrorMessages] = useState<string[]>([]);

  const tasksResponse = useModelTasks(model.id);

  const reCreate = useCallback(
    () => recreateThreediModel(model, callbackWhenModelDeleted),
    [model, callbackWhenModelDeleted]
  );

  const deleteModel = useCallback(
    () =>
      deleteThreediModel(model, (messages) => {
        setDeleteModelErrorMessages(messages);
        callbackWhenModelDeleted();
      }),
    [model, callbackWhenModelDeleted]
  );

  return (
    <>
      <p>There was an attempt to create a Rana model of this revision, but it failed.</p>
      {tasksResponse.isSuccess ? (
        <>
          <p>The tasks were:</p>
          <TasksTable tasks={tasksResponse.data.results} />
          <ButtonWithConfirmation
            buttonText="Re-generate this Rana model."
            dialogTitle="Really re-generate?"
            confirmationButtonText="Re-generate"
            confirmationButtonSafety="neutral"
            onClick={reCreate}
            marginRight="0.5rem"
            marginY="0.25rem"
            buttonIcon={<RevokeIcon boxSize={5} />}
            disableAfterClick
          >
            <p>
              This runs the model generation process again. If the previous model generation of this
              revision failed and there has been no new release of the HCC framework, it is likely
              to fail again.
            </p>
            <p>Re-generate this model?</p>
          </ButtonWithConfirmation>
          <ButtonWithConfirmation
            buttonText="Delete this Rana model"
            dialogTitle="Really delete?"
            confirmationButtonText="Delete"
            confirmationButtonSafety="dangerous"
            onClick={deleteModel}
            marginY="0.25rem"
            buttonIcon={<DeleteIcon boxSize={5} />}
            disableAfterClick
          >
            <p>This deletes this Rana model.</p>
            <p>Delete it?</p>
          </ButtonWithConfirmation>
          {deleteModelErrorMessages.map((errorMsg, idx) => (
            <AlertBox status={"error"} title={"Deletion failed!"} key={`${errorMsg}-${idx}`}>
              {errorMsg}
            </AlertBox>
          ))}
        </>
      ) : null}
    </>
  );
};

export default ModelWithoutSuccess;
